import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { BD_ARMY_RANKS, registerOfficerRequestSchema } from '@cosvision/shared';
import { useAuthStore } from '../store/auth-store.js';
import { ApiClientError } from '../lib/api.js';
import { useRegisterMutation } from '../hooks/useAuth.js';

export function RegisterPage() {
  const user = useAuthStore((s) => s.user);

  const [fullName, setFullName] = useState('');
  const [rank, setRank] = useState('');
  const [baNumber, setBaNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const mut = useRegisterMutation();

  // Authenticated users have no business on the register page.
  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    // Validate client-side against the same shared schema the server uses, so
    // empty/invalid forms never make a round-trip and the user sees inline
    // errors immediately.
    const parsed = registerOfficerRequestSchema.safeParse({
      fullName: fullName.trim(),
      rank,
      baNumber: baNumber.trim(),
      password,
      confirmPassword,
    });
    if (!parsed.success) {
      const errs: Record<string, string[]> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        (errs[key] ??= []).push(issue.message);
      }
      setFieldErrors(errs);
      return;
    }

    try {
      await mut.mutateAsync(parsed.data);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.errors) setFieldErrors(err.errors);
        if (!err.errors || Object.keys(err.errors).length === 0) setFormError(err.message);
      } else {
        setFormError('Registration failed. Please try again.');
      }
    }
  }

  const submitting = mut.isPending;

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-bn-800 to-bn-950 p-4">
      <svg
        aria-hidden="true"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <g
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="1.4"
          transform="translate(420 360)"
        >
          <ellipse rx="80" ry="46" />
          <ellipse rx="160" ry="92" />
          <ellipse rx="240" ry="138" />
          <ellipse rx="320" ry="184" />
          <ellipse rx="400" ry="230" />
          <ellipse rx="480" ry="276" />
          <ellipse rx="560" ry="322" />
        </g>
      </svg>

      <div className="relative w-full max-w-md card">
        <div className="text-center">
          <img
            src="/11sigbn-logo.png"
            alt="11 Signal Battalion"
            className="mx-auto h-20 w-20 object-contain"
          />
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-bn-700">
            11 Signal Battalion
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Officer Registration</h1>
        </div>

        {submitted ? (
          <div className="mt-6 space-y-4 text-center">
            <div className="rounded-md bg-rag-green-soft px-4 py-5 text-sm text-rag-green">
              <p className="font-semibold">Registration submitted.</p>
              <p className="mt-1">
                Your account will be activated once the Commanding Officer approves it. You&rsquo;ll
                be able to sign in with your BA Number after approval.
              </p>
            </div>
            <Link to="/login" className="btn-primary inline-block w-full">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-1 text-center text-sm text-slate-500">
              Create your account — the CO approves it before first sign-in.
            </p>
            <form
              className="mt-6 space-y-4"
              onSubmit={(e): void => void handleSubmit(e)}
              noValidate
            >
              <Field label="Full name" errors={fieldErrors.fullName}>
                <input
                  type="text"
                  autoComplete="name"
                  className="form-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={submitting}
                />
              </Field>

              <Field label="Rank" errors={fieldErrors.rank}>
                <select
                  className="form-input"
                  value={rank}
                  onChange={(e) => setRank(e.target.value)}
                  disabled={submitting}
                >
                  <option value="" disabled>
                    Select your rank…
                  </option>
                  {BD_ARMY_RANKS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="BA Number"
                errors={fieldErrors.baNumber}
                hint="Your service number — used to sign in."
              >
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="BA-12345"
                  className="form-input"
                  value={baNumber}
                  onChange={(e) => setBaNumber(e.target.value)}
                  disabled={submitting}
                />
              </Field>

              <Field label="Password" errors={fieldErrors.password}>
                <input
                  type="password"
                  autoComplete="new-password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </Field>

              <Field label="Confirm password" errors={fieldErrors.confirmPassword}>
                <input
                  type="password"
                  autoComplete="new-password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={submitting}
                />
              </Field>

              {formError && (
                <div
                  role="alert"
                  className="rounded-md bg-rag-red-soft px-3 py-2 text-sm text-rag-red"
                >
                  {formError}
                </div>
              )}

              <button type="submit" className="btn-primary w-full" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Register'}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-bn-700 hover:text-bn-600">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  errors?: string[];
  hint?: string;
  children: ReactNode;
}

/**
 * Wrapping `<label>` so the contained input/select is implicitly associated
 * with its label text — accessible without hand-managing matching ids.
 */
function Field({ label, errors, hint, children }: FieldProps) {
  return (
    <label className="block">
      <span className="form-label">{label}</span>
      {children}
      {hint && !errors?.length && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
      {errors?.map((m) => (
        <p key={m} className="form-error">
          {m}
        </p>
      ))}
    </label>
  );
}
